import type {
  Dispatch,
  KeyboardEventHandler,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
  SetStateAction
} from 'react';

import type {
  AvatarProps,
  ComboBoxProps,
  DateTimeDisplayProps,
  FileItemProps,
  FileListProps,
  MenuItemProps,
  MenuProps,
  NoChildrenProp,
  OmitStrict,
  WithAttributes
} from '@pega/cosmos-react-core';
import type {
  InteractionRenderers,
  MDMap,
  RichTextEditorProps,
  RichTextEditorState,
  RichTextViewerProps
} from '@pega/cosmos-react-rte';
import type { BaseDialogProps } from '@pega/cosmos-react-core/lib/components/Dialog/Dialog.types';

import type { OpenGraphData } from '../OpenGraphPreview/OpenGraphPreview';

/*
 * ************************************************************
 *  Internal interfaces
 * ************************************************************
 */

export interface SubComponentVariant {
  variant: 'post' | 'reply';
}

export interface FeedAttachmentsProps
  extends SubComponentVariant,
    Pick<FileListProps, 'onDownloadAll'> {
  attachments?: FileItemProps[];
  /* OpenGraph data for an external link. If provided will render a link card. */
  openGraphData?: OpenGraphData;
  disabled?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export interface PostVisibilityControlProps extends Pick<FeedNewPostProps, 'visibilityMode'> {
  /* Default visibility set for the post. */
  visibility: Visibility;
  /* State setter for selecting visibility. */
  setVisibility?: Dispatch<SetStateAction<Visibility>>;
  /** Props for the private recipient control. */
  recipientInput: RecipientInputProps;
}

export interface CustomPostSubmitEvent {
  /* The id of the post. */
  id: FeedNewPostProps['id'];
  /* The username of the post author. */
  username: string;
  /** The object identifying the type of the post. */
  postType?: PostType;
  /* The visibility of the post. */
  visibility?: Visibility;
  /* A list of recipients who have been selected to view the post. If no recipients have been added, then the post visibility is public. */
  recipients?: RecipientMenuItem[];
}

export interface DefaultPostSubmitEvent extends CustomPostSubmitEvent {
  /* The markdown content of the post. */
  value: string;
  /* A list of attachments. */
  attachments: FileItemProps[];
  /* A function to clear any recipients selected for private posts. */
  clear: () => void;
}

interface FeedBaseInputRegionProps extends SubComponentVariant, FeedCompositionBaseProps {
  recipients?: RecipientMenuItem[];
  postType?: PostType;
  postTypeMenu?: ReactNode;
  visibility?: Visibility;
  visibilityControl?: ReactNode;
}

export interface FeedInputRegionProps extends FeedBaseInputRegionProps, DefaultPostConfig {
  rteRef: RefObject<RichTextEditorState>;
}

export type FeedCustomInputRegionProps = FeedBaseInputRegionProps & CustomPostConfig;

export interface FeedEditRegionProps
  extends SubComponentVariant,
    DefaultEditConfig,
    Pick<FeedPostBaseProps, 'id'>,
    Pick<FeedPostProps, 'visibility' | 'recipients'> {
  content: RichTextEditorProps['defaultValue'];
  emptyEditText: boolean;
  setEmptyEditText: Dispatch<SetStateAction<boolean>>;
}

export type FeedCustomEditRegionProps = CustomEditConfig &
  Pick<FeedPostBaseProps, 'id'> &
  Pick<FeedPostProps, 'visibility' | 'recipients'>;

export interface FeedModalListProps {
  /* The count for the list of items. */
  count?: number;
  /* The heading for the Modal. */
  heading: string;
  /* The list of MenuItems to render in the Modal. */
  listItems: MenuItemProps[];
  /* A callback that is triggered when the bottom of the Modal list is scrolled into view so that additional data may be displayed. */
  onLoadMore?: () => void;
  /* Indicates if the data is being currently loading */
  loading?: boolean;
}

export interface AutopilotInfo {
  id: string;
  label: string;
  meta?: string;
  description?: string;
}

export interface FeedContentProps {
  children: ReactElement | string;
  /*
   * A maximum height in pixels to display post content within before prompting to expand the post.
   * @default Infinity
   */
  maxContentHeight?: number;
  onLinkRender?: RichTextViewerProps['onMarkdownLinkRender'];
  ref?: Ref<HTMLDivElement>;
}

export type FeedContentHeaderProps = SubComponentVariant &
  Pick<
    FeedPostBaseProps,
    | 'id'
    | 'fullname'
    | 'username'
    | 'postTimestamp'
    | 'avatarSrc'
    | 'icon'
    | 'userStatus'
    | 'menuItems'
    | 'edited'
    | 'editTimestamp'
    | 'onEdit'
    | 'onUserClick'
  > &
  Pick<
    FeedPostProps,
    | 'visibility'
    | 'postType'
    | 'customPostType'
    | 'postContext'
    | 'recipients'
    | 'recipientsCount'
    | 'recipientsLoading'
    | 'onLoadMoreRecipients'
    | 'onRecipientInteraction'
  >;

export type FeedContentFooterProps = SubComponentVariant &
  Pick<
    FeedPostBaseProps,
    | 'id'
    | 'username'
    | 'liked'
    | 'likes'
    | 'likeCount'
    | 'interactionsEnabled'
    | 'onCommentClick'
    | 'onLikeClick'
    | 'onLikeCountInteraction'
    | 'onLoadMoreLikes'
    | 'likesLoading'
  > &
  Pick<
    FeedPostProps,
    'replies' | 'replyCount' | 'onShowMoreReplies' | 'onShowLessReplies' | 'repliesLoading'
  >;

/*
 * ************************************************************
 *  External interfaces
 * ************************************************************
 */

export interface Filter {
  /* Sets DOM id for the control and associates label element via 'for' attribute. */
  id: string;
  /* Pass a string or a fragment with an Icon and string for the filter label. */
  label: ReactNode;
  /*
   * Disables the filter.
   * @default false
   */
  disabled?: boolean;
  /*
   * Sets on prop via onFilterChange.
   * @default false
   */
  on?: boolean;
}

export interface FeedRichTextProps
  extends Pick<
      RichTextEditorProps,
      | 'label'
      | 'onChange'
      | 'onFocus'
      | 'onBlur'
      | 'height'
      | 'defaultValue'
      | 'labelHidden'
      | 'disabled'
      | 'readOnly'
    >,
    Pick<FeedProps, 'onLinkRender'> {
  onKeyDown?: KeyboardEventHandler;
}

export type SearchType = { id: string; label: string; icon?: string };

export type FeedProps = WithAttributes<
  'div',
  NoChildrenProp & {
    /* Title for the feed. */
    title: string;
    /* Information related to the user of the Feed. */
    userInfo: {
      username: string;
      fullname: string;
      avatarSrc?: string;
      userStatus?: AvatarProps['status'];
      meta?: string[];
      timeZone?: string;
    };
    /* A set of search types that can be used to determine which data set to conduct a mention search on. */
    searchTypes?: SearchType[];
    /* A callback that runs when a decorator search has been run inside of the feed. */
    onDecoratorSearch?: (e: {
      type: 'mention' | 'tag';
      search: string;
      mentionType: string;
    }) => void;
    /* Results that will be shown in a Popover for a mention or tag search. */
    searchResults?: MenuItemProps[];
    /* Information related to the Autopilot mention. */
    autopilotInfo?: AutopilotInfo;
    /* A callback that is triggered when an Autopilot mention is clicked. The callback sends back the target of the element. The callback should return a function component. */
    onAutopilotClick?: (e: {
      target?: BaseDialogProps['target'];
      onDismiss?: () => void;
    }) => JSX.Element | undefined;
    /* A callback that is triggered when a mention button is clicked. The callback sends back the id, text, and type of the mention. The callback should return a function component. */
    onMentionClick?: (e: {
      id: string;
      text: string;
      type: string;
      target?: BaseDialogProps['target'];
      closeGlimpse?: () => void;
    }) => JSX.Element | undefined;
    /* A callback that is triggered when a mention link is previewed. The callback sends back the id, text, and type of the mention. */
    onMentionPreview?: (e: { id: string; text: string; type: string }) => void;
    /* A callback that is triggered when a hashtag is clicked. The callback sends back the test of the tag. */
    onTagClick?: (tag: string) => void;
    /* A list of configuration objects used to render custom components within Feed posts and replies based off of a given regex pattern. */
    interactionRenderers?: InteractionRenderers[];
    /* A mapping for various element types to render custom components for within posts. */
    markdownMap?: MDMap;
    /* A set of filters to apply to the Feed. */
    filters?: Filter[];
    /* A callback that runs when the filter is toggled. */
    onFilterChange?: (filterId: string, on: boolean) => void;
    /* A callback that is triggered when the bottom of the Feed is scrolled into view so that additional posts may be displayed. */
    onLoadMore?: () => void;
    /* Indicates if the posts are being currently loading */
    loadingPosts?: boolean;
    /* Indicates if the feed is in initial loading state */
    progress?: boolean;
    /* A region to hold the new post component */
    newPostRegion?: ReactNode;
    /* A list of FeedPost components */
    posts?: ReactNode[];
    /* A callback that runs when a link is rendered in the RichTextViewer. */
    onLinkRender?: (href: string, signal: AbortSignal) => Promise<OpenGraphData>;
  }
>;

export type Visibility = 'public' | 'private';

export type PostType = { id: string; label: string; icon?: string };

export type RecipientMenuItem = Pick<
  MenuItemProps,
  'id' | 'selected' | 'primary' | 'secondary' | 'visual'
>;

export interface RecipientInputProps
  extends Pick<MenuProps, 'loadMore' | 'loading'>,
    Pick<ComboBoxProps, 'onMenuOpen'> {
  /* Array of private recipient options. */
  availableRecipients: RecipientMenuItem[];
  /* Array of selected private recipients. */
  recipients: RecipientMenuItem[];
  /* Callback that returns the modified search string. */
  onSearch: (search: string) => void;
  /* Callback that is triggered when a recipient is selected. */
  onAdd: (id: MenuItemProps['id']) => void;
  /* Callback that is triggered when a recipient is deselected. */
  onRemove: (id: MenuItemProps['id']) => void;
  /* Callback that is triggered when a recipient is selected or deselected. */
  onToggle?: (id: MenuItemProps['id']) => void;
}

interface FeedCompositionBaseProps {
  /* A unique ID for the post. */
  id: string;
  /* Region to render any banners associated with the post. */
  banners?: ReactNode;
  /* If true, a progress indicator will overlay content. */
  progress?: boolean;
  /* Callback fired when user requests AI text rewrite. DX component should handle the data page call and loading state, then call onSuccess with the rewritten text. */
  onRewriteClick?: (
    originalText: string,
    rewriteAction: string,
    additionalInstructions: string,
    onSuccess: (rewrittenText: string) => void,
    isEditContext: boolean
  ) => void;
}

export interface DefaultPostConfig extends Pick<FeedRichTextProps, 'defaultValue'> {
  /* A set of attachment files. */
  attachments?: FileItemProps[];
  /* A callback containing a list of added files that will run whenever files are added to the message. */
  onFilesAdded?: (files: File[]) => void;
  /* Callback that runs when the submit button has been clicked. */
  onSubmit: (event: DefaultPostSubmitEvent) => void;
  /** Use to capture the editor's uncontrolled value when the component unmounts. */
  onUnmount?: (rteValue: string) => void;
}

export interface CustomPostConfig {
  /* The content composing the custom post type. */
  content: ReactNode;
  /* Callback function for the submit button. */
  onSubmit: (event: CustomPostSubmitEvent) => void;
  /* Callback function for the cancel button. */
  onCancel: (id: FeedNewPostProps['id']) => void;
}

export type FeedNewPostProps = WithAttributes<
  'div',
  FeedCompositionBaseProps & {
    /*
     * What visibility controls are available for this post.
     * @default 'public'
     * */
    visibilityMode?: Visibility | 'selectable';
    /** Props for the private recipient Combobox. */
    recipientInput?: RecipientInputProps;
    /* Configuration for the default post type. */
    defaultConfig?: DefaultPostConfig;
    /* Configuration for custom post types. */
    customConfig?: CustomPostConfig;
    /* The type of the post. */
    postType?: PostType;
    /* Array of available post types. */
    postTypes?: PostType[];
    /* Callback that is triggered when the post type selection is updated. */
    onPostTypeChange?: (postType: PostType) => void;
  }
>;

export type FeedReplyInputProps = WithAttributes<
  'div',
  FeedCompositionBaseProps &
    DefaultPostConfig & {
      /* Callback that sets returns a shared ref to the RTE component */
      onSetInputRef?: (event: { ref: RefObject<RichTextEditorState> }) => void;
    }
>;

export interface FeedPostBaseProps {
  /* Unique ID for the post. */
  id: string;
  /* Display name for the message author. */
  fullname: string;
  /* Username for the message author. */
  username: string;
  /* A timestamp of when the message was initially submitted. */
  postTimestamp: DateTimeDisplayProps['value'];
  /* The message content. */
  content: ReactElement | string;
  /* Attachments on the message. */
  attachments?: FileItemProps[];
  /* An image source for the message author's Avatar. */
  avatarSrc?: string;
  /* The name of an icon to render within the Avatar. */
  icon?: string;
  /* The status of the message author. */
  userStatus?: AvatarProps['status'];
  /* Set true if the user has liked the message. */
  liked?: boolean;
  /* A set of users that have liked the message. */
  likes?: MenuItemProps[];
  /* The total count of likes on the message. */
  likeCount?: number;
  /*
   * If true, the like and comment buttons will be enabled.
   * @default true
   */
  interactionsEnabled?: boolean;
  /*
   * A maximum height in pixels to display message content within before prompting to expand the message.
   * @default Infinity
   */
  maxContentHeight?: FeedContentProps['maxContentHeight'];
  /* A set of menu options for actions on the message. */
  menuItems?: MenuItemProps[];
  /* Callback that is triggered when the edit action is clicked. If undefined, the message will not be editable. */
  onEdit?: (event: {
    id: string;
    postType?: PostType;
    visibility?: Visibility;
    recipients?: RecipientMenuItem[];
  }) => void;
  /*
   * Flag that indicates if a message has been previously edited.
   * @default false
   */
  edited?: boolean;
  /* A timestamp of when the message was last edited. This will only display if edited prop is true. */
  editTimestamp?: DateTimeDisplayProps['value'];
  /* Callback that runs when the mouse pointer hovers over the message. */
  onMouseEnter?: (event: { id: string }) => void;
  /* Callback that runs when the mouse pointer leaves the message. */
  onMouseLeave?: (event: { id: string }) => void;
  /* Callback that runs when the author's name or Avatar has been clicked. */
  onUserClick?: (event: { id: string; username: string }, clickEvent?: MouseEvent) => void;
  /* Callback that runs when the comment button has been clicked. */
  onCommentClick: (event: { id: string; username: string }) => void;
  /* Callback that runs when the like button has been clicked. */
  onLikeClick: (event: { id: string; user: MenuItemProps }) => void;
  /* Callback that runs when the like count on a message is hovered, focused, or clicked. */
  onLikeCountInteraction?: () => void;
  /* Callback that runs when the bottom of the extended like list Modal is reached, allowing for more likes to be loaded. */
  onLoadMoreLikes?: () => void;
  /* A loading indicator for the likes Modal. */
  likesLoading?: boolean;
}

export interface DefaultEditConfig
  extends Pick<FeedRichTextProps, 'defaultValue'>,
    Pick<FeedCompositionBaseProps, 'progress' | 'banners' | 'onRewriteClick'> {
  editContent?: never;
  /* Callback function for the update button. */
  onEditSubmit: (event: {
    id: FeedPostBaseProps['id'];
    value: string;
    visibility?: Visibility;
    recipients?: RecipientMenuItem[];
  }) => void;
  /* Callback function for the cancel button. */
  onEditCancel?: (id: FeedPostBaseProps['id']) => void;
  /** Props for the private recipient Combobox. */
  recipientInput?: RecipientInputProps;
  /** Use to capture the editor's uncontrolled value when the component unmounts. */
  onUnmount?: (rteValue: string) => void;
}

export interface CustomEditConfig extends Pick<FeedCompositionBaseProps, 'progress' | 'banners'> {
  /* The content composing the custom post type. */
  editContent: ReactNode;
  /* Callback function for the update button. */
  onEditSubmit: (event: {
    id: FeedPostBaseProps['id'];
    visibility?: Visibility;
    recipients?: RecipientMenuItem[];
  }) => void;
  /* Callback function for the cancel button. */
  onEditCancel: (id: FeedPostBaseProps['id']) => void;
  /** Props for the private recipient Combobox. */
  recipientInput?: RecipientInputProps;
}

export type FeedPostProps = WithAttributes<
  'div',
  FeedPostBaseProps & {
    /* Configuration for editing the post. */
    editConfig?: DefaultEditConfig | CustomEditConfig;
    /*
     * The type of message. If private, then only the designated recipients will be able to view the message.
     * @default 'public'
     */
    visibility?: Visibility;
    /* The type of the post. */
    postType?: PostType;
    /* Indicates post is non-standard. Used for visual differentiation. */
    customPostType?: boolean;
    /* The set of recipients assigned to a private message. */
    recipients?: RecipientMenuItem[];
    /* The total number of private recipients. */
    recipientsCount?: number;
    /* The context of the message. */
    postContext?: ReactElement | string;
    /* Callback that runs when the private recipient icon on a post is hovered, focused, or clicked. */
    onRecipientInteraction?: () => void;
    /* Callback that runs when the bottom of the extended private recipient list Modal is reached, allowing for more recipients to be loaded. */
    onLoadMoreRecipients?: () => void;
    /* A loading indicator for the recipients Modal. */
    recipientsLoading?: boolean;
    /* Props that will be passed into the reply input, see FeedReplyInput for more information. */
    replyInput: OmitStrict<FeedCompositionBaseProps, 'id'> & DefaultPostConfig;
    /* Determines if the reply input should be rendered. */
    replyInputShown?: boolean;
    /* An array to of replies for this message. */
    replies?: ReactElement[];
    /* A total count of all replies. */
    replyCount?: number;
    /* Callback that runs when the user clicks Show more button for the replies. */
    onShowMoreReplies?: (event: { id: FeedPostBaseProps['id'] }) => void;
    /* Callback that runs when the user clicks Show less button for the replies. */
    onShowLessReplies?: (event: { id: FeedPostBaseProps['id'] }) => void;
    /* A loading indicator for the replies. */
    repliesLoading?: boolean;
  }
>;

export type FeedReplyProps = WithAttributes<
  'div',
  FeedPostBaseProps & {
    /* Configuration for editing the post. */
    editConfig?: DefaultEditConfig;
  }
>;
